import React, { useState, useEffect, useCallback } from 'react';
import { useApp } from '../context/AppContext';
import { InstructionGuide } from '../components/layout/InstructionGuide';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Store, Plus, Edit2, Trash2, TrendingUp, TrendingDown, DollarSign, FileDown } from 'lucide-react';
import { toast } from 'sonner';

const emptyForm = { name: '', sp_type: 'business', business_place_id: '', owner_name: '', contact: '', address: '', city: '', zone: '', status: 'active', monthly_target: 0, current_revenue: 0, previous_revenue: 0 };

export default function SalesPoints() {
  const { api, userZone, selectedBusiness } = useApp();
  const [salesPoints, setSalesPoints] = useState([]);
  const [places, setPlaces] = useState([]);
  const [activeTab, setActiveTab] = useState('business');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const load = useCallback(async () => {
    try {
      const params = userZone ? { zone: userZone } : {};
      if (selectedBusiness) params.business_id = selectedBusiness;
      const [spRes, pRes] = await Promise.all([api.getSalesPoints(params), api.getBusinessPlaces(params)]);
      setSalesPoints(spRes.data);
      setPlaces(pRes.data);
    } catch (e) { console.error(e); }
  }, [api, userZone, selectedBusiness]);

  useEffect(() => { load(); }, [load]);

  const filtered = salesPoints.filter(sp => sp.sp_type === activeTab);

  const openCreate = () => { setEditing(null); setForm({ ...emptyForm, sp_type: activeTab }); setDialogOpen(true); };
  const openEdit = (sp) => {
    setEditing(sp);
    setForm({
      name: sp.name, sp_type: sp.sp_type, business_place_id: sp.business_place_id || '', owner_name: sp.owner_name || '',
      contact: sp.contact || '', address: sp.address || '', city: sp.city || '', zone: sp.zone || '', status: sp.status,
      monthly_target: sp.monthly_target || 0, current_revenue: sp.current_revenue || 0, previous_revenue: sp.previous_revenue || 0,
    });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!form.name) { toast.error('Name is required'); return; }
    try {
      const data = { ...form, monthly_target: Number(form.monthly_target), current_revenue: Number(form.current_revenue), previous_revenue: Number(form.previous_revenue) };
      if (selectedBusiness && !editing) data.business_id = selectedBusiness;
      if (editing) {
        await api.updateSalesPoint(editing.id, data);
        toast.success('Sales point updated');
      } else {
        await api.createSalesPoint(data);
        toast.success('Sales point created');
      }
      setDialogOpen(false);
      load();
    } catch (e) { toast.error('Failed to save'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this sales point?')) return;
    try {
      await api.deleteSalesPoint(id);
      toast.success('Sales point removed');
      load();
    } catch (e) { toast.error('Failed to delete'); }
  };

  const getGrowth = (sp) => {
    if (!sp.previous_revenue || sp.previous_revenue === 0) return null;
    return (((sp.current_revenue - sp.previous_revenue) / sp.previous_revenue) * 100).toFixed(1);
  };

  const getPlaceName = (id) => places.find(p => p.id === id)?.name || '-';

  const totalRevenue = filtered.reduce((sum, sp) => sum + (sp.current_revenue || 0), 0);
  const totalTarget = filtered.reduce((sum, sp) => sum + (sp.monthly_target || 0), 0);

  return (
    <div data-testid="sales-points-page" className="space-y-6 fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">Sales Points</h1>
          <p className="text-sm text-slate-500 mt-1">{salesPoints.length} total sales points</p>
        </div>
        <div className="flex items-center gap-2">
          <a href={api.exportSalesPoints(userZone, activeTab)} download data-testid="export-sp-btn"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-border text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors">
            <FileDown className="w-4 h-4" /> Export
          </a>
          <Button data-testid="add-sales-point-btn" onClick={openCreate} className="gap-2 bg-amber-600 hover:bg-amber-700 text-white shadow-md">
            <Plus className="w-4 h-4" /> Add Sales Point
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="kpi-card">
          <div className="flex items-center gap-2 text-sm text-slate-500"><Store className="w-4 h-4" /> Total Points</div>
          <p className="text-2xl font-extrabold text-slate-900 mt-1">{filtered.length}</p>
        </div>
        <div className="kpi-card">
          <div className="flex items-center gap-2 text-sm text-slate-500"><DollarSign className="w-4 h-4" /> Revenue</div>
          <p className="text-2xl font-extrabold text-slate-900 mt-1">{(totalRevenue / 100000).toFixed(1)}L</p>
        </div>
        <div className="kpi-card">
          <div className="flex items-center gap-2 text-sm text-slate-500"><TrendingUp className="w-4 h-4" /> Target</div>
          <p className="text-2xl font-extrabold text-slate-900 mt-1">{(totalTarget / 100000).toFixed(1)}L</p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList data-testid="sp-type-tabs" className="bg-slate-100">
          <TabsTrigger value="business" data-testid="sp-tab-business" className="data-[state=active]:bg-amber-600 data-[state=active]:text-white">
            Business ({salesPoints.filter(s => s.sp_type === 'business').length})
          </TabsTrigger>
          <TabsTrigger value="individual" data-testid="sp-tab-individual" className="data-[state=active]:bg-amber-600 data-[state=active]:text-white">
            Individual ({salesPoints.filter(s => s.sp_type === 'individual').length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab}>
          <Card className="border border-border/60 shadow-sm">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50 border-b border-slate-200">
                    <TableHead className="text-slate-500 uppercase tracking-wider font-semibold text-xs">Name</TableHead>
                    <TableHead className="text-slate-500 uppercase tracking-wider font-semibold text-xs">Location</TableHead>
                    <TableHead className="text-slate-500 uppercase tracking-wider font-semibold text-xs">Revenue</TableHead>
                    <TableHead className="text-slate-500 uppercase tracking-wider font-semibold text-xs">Target</TableHead>
                    <TableHead className="text-slate-500 uppercase tracking-wider font-semibold text-xs">Growth</TableHead>
                    <TableHead className="text-slate-500 uppercase tracking-wider font-semibold text-xs">Status</TableHead>
                    <TableHead className="text-slate-500 uppercase tracking-wider font-semibold text-xs text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map(sp => {
                    const growth = getGrowth(sp);
                    return (
                      <TableRow key={sp.id} data-testid={`sp-row-${sp.id}`} className="border-b border-slate-100 hover:bg-slate-50/50 h-16">
                        <TableCell>
                          <div>
                            <p className="text-sm font-semibold text-slate-800">{sp.name}</p>
                            <p className="text-xs text-slate-400">{sp.owner_name}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <p className="text-sm text-slate-600">{sp.city}</p>
                          <p className="text-xs text-slate-400">{getPlaceName(sp.business_place_id)}</p>
                        </TableCell>
                        <TableCell className="text-sm font-bold text-slate-900">{(sp.current_revenue / 1000).toFixed(0)}K</TableCell>
                        <TableCell className="text-sm text-slate-600">{(sp.monthly_target / 1000).toFixed(0)}K</TableCell>
                        <TableCell>
                          {growth !== null ? (
                            <span className={`flex items-center gap-1 text-sm font-bold ${Number(growth) >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                              {Number(growth) >= 0 ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
                              {growth}%
                            </span>
                          ) : <span className="text-xs text-slate-400">N/A</span>}
                        </TableCell>
                        <TableCell>
                          <Badge variant={sp.status === 'active' ? 'default' : 'outline'} className={`text-xs ${sp.status === 'active' ? 'bg-emerald-100 text-emerald-700 border-emerald-200' : ''}`}>
                            {sp.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button data-testid={`edit-sp-${sp.id}`} variant="ghost" size="sm" onClick={() => openEdit(sp)} className="hover:bg-amber-50 hover:text-amber-700"><Edit2 className="w-4 h-4" /></Button>
                            <Button data-testid={`delete-sp-${sp.id}`} variant="ghost" size="sm" onClick={() => handleDelete(sp.id)} className="hover:bg-red-50 hover:text-red-600"><Trash2 className="w-4 h-4" /></Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {filtered.length === 0 && (
                    <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-8">No {activeTab} sales points</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="sales-point-dialog" className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold">{editing ? 'Edit' : 'Add'} Sales Point</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-4 max-h-[60vh] overflow-y-auto">
            <div className="col-span-2">
              <Label>Name</Label>
              <Input data-testid="sp-name-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Sales point name" />
            </div>
            <div>
              <Label>Type</Label>
              <Select value={form.sp_type} onValueChange={v => setForm({ ...form, sp_type: v })}>
                <SelectTrigger data-testid="sp-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="business">Business</SelectItem>
                  <SelectItem value="individual">Individual</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Business Place</Label>
              <Select value={form.business_place_id} onValueChange={v => setForm({ ...form, business_place_id: v })}>
                <SelectTrigger data-testid="sp-place-select"><SelectValue placeholder="Select place" /></SelectTrigger>
                <SelectContent>
                  {places.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Owner Name</Label>
              <Input data-testid="sp-owner-input" value={form.owner_name} onChange={e => setForm({ ...form, owner_name: e.target.value })} placeholder="Owner name" />
            </div>
            <div>
              <Label>Contact</Label>
              <Input data-testid="sp-contact-input" value={form.contact} onChange={e => setForm({ ...form, contact: e.target.value })} placeholder="Phone number" />
            </div>
            <div>
              <Label>City</Label>
              <Input data-testid="sp-city-input" value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} placeholder="City" />
            </div>
            <div>
              <Label>Zone</Label>
              <Input data-testid="sp-zone-input" value={form.zone} onChange={e => setForm({ ...form, zone: e.target.value })} placeholder="Zone" />
            </div>
            <div>
              <Label>Monthly Target</Label>
              <Input data-testid="sp-target-input" type="number" value={form.monthly_target} onChange={e => setForm({ ...form, monthly_target: e.target.value })} />
            </div>
            <div>
              <Label>Current Revenue</Label>
              <Input data-testid="sp-revenue-input" type="number" value={form.current_revenue} onChange={e => setForm({ ...form, current_revenue: e.target.value })} />
            </div>
            <div>
              <Label>Previous Revenue</Label>
              <Input data-testid="sp-prev-revenue-input" type="number" value={form.previous_revenue} onChange={e => setForm({ ...form, previous_revenue: e.target.value })} />
            </div>
            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={v => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="sp-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button data-testid="save-sp-btn" onClick={handleSubmit} className="bg-amber-600 hover:bg-amber-700 text-white">{editing ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
